/**
 * Live Email API Test Runner Component
 * 
 * UI for running comprehensive API tests and displaying results
 */

import React, { useState, useRef } from 'react';
import { liveEmailApiTestSuite } from '../services/__tests__/liveEmailApiTests';
import { Download, Play, Loader } from 'lucide-react';

interface TestResult {
  testName: string;
  category: string;
  status: 'PASS' | 'FAIL' | 'SKIP';
  timestamp: string;
  duration: number;
  request?: any;
  response?: any;
  error?: string;
  details: string;
}

export function TestRunner() {
  const [results, setResults] = useState<TestResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [currentTest, setCurrentTest] = useState<string>('');
  const reportRef = useRef<string>('');

  const handleRunTests = async () => {
    setIsRunning(true);
    setResults([]);
    setCurrentTest('Initializing...');

    try {
      const testResults = await liveEmailApiTestSuite.runAllTests();
      setResults(testResults);
      reportRef.current = liveEmailApiTestSuite.generateHtmlReport();
      setCurrentTest('Tests completed!');
    } catch (error) {
      setCurrentTest(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsRunning(false);
    }
  };

  const handleDownloadReport = () => {
    const element = document.createElement('a');
    element.setAttribute(
      'href',
      'data:text/html;charset=utf-8,' + encodeURIComponent(reportRef.current)
    );
    element.setAttribute('download', `email-api-test-report-${Date.now()}.html`);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const stats = {
    total: results.length,
    passed: results.filter(r => r.status === 'PASS').length,
    failed: results.filter(r => r.status === 'FAIL').length,
    skipped: results.filter(r => r.status === 'SKIP').length
  };

  return (
    <div className="w-full bg-gradient-to-br from-slate-900 to-slate-800 rounded-lg p-8 text-white">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">🚀 Live Email API Test Suite</h1>
        <p className="text-slate-400">
          Comprehensive testing of email operations, AI agents, and database queries
        </p>
      </div>

      {/* Control Panel */}
      <div className="bg-slate-800 rounded-lg p-6 mb-8">
        <button
          onClick={handleRunTests}
          disabled={isRunning}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 px-6 py-3 rounded-lg font-semibold transition"
        >
          {isRunning ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              Running Tests...
            </>
          ) : (
            <>
              <Play className="w-5 h-5" />
              Run All Tests
            </>
          )}
        </button>

        {currentTest && (
          <div className="mt-4 text-sm text-slate-300">
            Current: {currentTest}
          </div>
        )}
      </div>

      {/* Statistics */}
      {stats.total > 0 && (
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-700 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold">{stats.total}</div>
            <div className="text-slate-400 text-sm">Total Tests</div>
          </div>
          <div className="bg-green-900 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-green-400">{stats.passed}</div>
            <div className="text-slate-400 text-sm">Passed</div>
          </div>
          <div className="bg-red-900 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-red-400">{stats.failed}</div>
            <div className="text-slate-400 text-sm">Failed</div>
          </div>
          <div className="bg-yellow-900 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-yellow-400">{stats.skipped}</div>
            <div className="text-slate-400 text-sm">Skipped</div>
          </div>
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Test Results</h2>
            <button
              onClick={handleDownloadReport}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 px-4 py-2 rounded-lg text-sm font-semibold transition"
            >
              <Download className="w-4 h-4" />
              Download HTML Report
            </button>
          </div>

          {results.map((result, index) => (
            <div
              key={index}
              className={`rounded-lg p-4 border-l-4 ${
                result.status === 'PASS'
                  ? 'bg-green-900/20 border-green-500'
                  : result.status === 'FAIL'
                  ? 'bg-red-900/20 border-red-500'
                  : 'bg-yellow-900/20 border-yellow-500'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold">{result.testName}</div>
                <div
                  className={`px-3 py-1 rounded text-xs font-bold ${
                    result.status === 'PASS'
                      ? 'bg-green-500/20 text-green-300'
                      : result.status === 'FAIL'
                      ? 'bg-red-500/20 text-red-300'
                      : 'bg-yellow-500/20 text-yellow-300'
                  }`}
                >
                  {result.status}
                </div>
              </div>
              <div className="text-sm text-slate-400 mb-2">
                {result.category} • {result.duration}ms
              </div>
              <div className="text-sm mb-2">{result.details}</div>
              {result.response && (
                <div className="bg-slate-900 rounded p-3 text-xs font-mono overflow-x-auto max-h-48 overflow-y-auto">
                  <pre>{JSON.stringify(result.response, null, 2)}</pre>
                </div>
              )}
              {result.error && (
                <div className="bg-red-900/30 rounded p-3 text-xs text-red-300">
                  <strong>Error:</strong> {result.error}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {results.length === 0 && !isRunning && (
        <div className="text-center py-12 text-slate-400">
          <p className="text-lg">Click "Run All Tests" to start the comprehensive test suite</p>
        </div>
      )}
    </div>
  );
}
